import type { GridProps } from '@chakra-ui/react'
import { Box, Divider, Grid, Spinner, Text } from '@chakra-ui/react'
import type { InsuranceBase, Offer, OfferProperty } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { useEffect, useRef, useState } from 'react'

import { PropertyRecapTable } from '@/components/Recap/RecapQuote'
import { RecapTable } from '@/components/RecapTable'
import type { RecapTableProps } from '@/components/RecapTable/RecapTable'
import { DateSelector } from '@/containers/dashboard/Stats/DateSelector'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'

import { CompanyLogo } from '../../../../../components/CompanyLogo'
import { CompletedOverview } from '../../../../../components/CompletedOverview'
import type { CompletedOverviewItemProps } from '../../../../../components/CompletedOverview/CompletedOverviewItem'
import { RecapSummary } from '../../../../../components/RecapSummary'
import { RecapSidebar, SectionItem } from '../../../../../components/RecapSummary/RecapSidebar'
import { useGetInsurerInfo } from '../../../../../hooks'

const InformationTab = {
  Recap: 0,
  Info: 1,
  AdditionalDates: 2
}
type InformationTab = typeof InformationTab[keyof typeof InformationTab]

type InformationProps = {
  number?: number | React.ReactNode
  sectionTitle?: string
  offer: Offer
  insurance: InsuranceBase
  recapProps: RecapTableProps
  completedOverviewData: CompletedOverviewItemProps[]
  completedOverviewProps?: GridProps
  additionalInfoData?: CompletedOverviewItemProps[]
  additionalInfoProps?: GridProps
  sidebarSectionItems?: React.ReactNode[]
  additionalDatesTabData?: CompletedOverviewItemProps[]
  additionalDatesTabProps?: GridProps
  sjednaniInfoData?: CompletedOverviewItemProps[]
  sjednaniInfoProps?: GridProps
}

export const Information = ({
  number,
  sectionTitle,
  offer,
  recapProps,
  completedOverviewData,
  additionalInfoData,
  completedOverviewProps,
  additionalInfoProps,
  sidebarSectionItems,
  additionalDatesTabData,
  additionalDatesTabProps,
  sjednaniInfoData,
  sjednaniInfoProps
}: InformationProps) => {
  const { app } = useApp()
  const completedOverviewRef = useRef<HTMLDivElement>(null)
  const [logoWrapperHeight, setLogoWrapperHeight] = useState<number | undefined>(undefined)

  useEffect(() => {
    const completedOverviewElement = completedOverviewRef.current

    if (completedOverviewElement) {
      setLogoWrapperHeight(completedOverviewElement?.clientHeight)
    }
  }, [])

  const { data: dataInsurer, isLoading: isLoadingInsurer } = useGetInsurerInfo(offer.insurerId, { retry: false })
  const [selectedTab, setSetselectedTab] = useState<InformationTab>(InformationTab.Info)

  const tabs = [
    { name: 'Informace', value: InformationTab.Info },
    { name: 'Rekapitulace', value: InformationTab.Recap }
  ]

  if (additionalDatesTabData && additionalDatesTabData.length > 0) {
    tabs.push({ name: 'Doplňující datumy', value: InformationTab.AdditionalDates })
  }

  return (
    <RecapSummary
      number={number}
      offer={offer}
      sectionTitle={sectionTitle}
      wrapperProps={{
        sx: {
          gridTemplateRows: { lg: 'auto 1fr' },
        },
      }}
      bodyProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 6,
          // gridTemplateRows: 'auto auto 1fr auto',
        },
      }}
      sidebar={
        <RecapSidebar
          logo={<CompanyLogo insurer={{ id: offer.insurerId, name: offer.insurerName }} p={8} maxW='240px' offerType={offer.type} />}
          website={isLoadingInsurer ? <Spinner /> : dataInsurer?.data?.url}
          email={isLoadingInsurer ? <Spinner /> : dataInsurer?.data?.email}
          // logoWrapperHeight={logoWrapperHeight}
          paymentFrequencyId={offer.paymentFrequencyId!}
          paymentPrice={offer.priceFrequency}
          totalPrice={offer.price}
          completed={true}
          sectionItems={[
            (
              <SectionItem label={`${(offer.paymentFrequencyId == Selection.OneTime ? 'Celkem' : 'Roční pojistné')}`}>
                {/* <SectionItem label="Roční pojistné"> */}
                <Text sx={{ fontSize: 'lg', fontWeight: 'bold' }}>
                  {priceFormat(offer.price)}
                </Text>
              </SectionItem>
            ),
            ...(sidebarSectionItems ?? [])
          ]} />
      }
    >
      {selectedTab == InformationTab.Info &&
                <>
                  <CompletedOverview
                    ref={completedOverviewRef}
                    items={completedOverviewData}
                    {...completedOverviewProps}
                  />

                  {
                    sjednaniInfoData &&
                        sjednaniInfoData.length > 0 &&
                        completedOverviewData &&
                        completedOverviewData.length > 0 &&
                        <Divider />
                  }

                  {/* Sekce pro další info o sjednání - např pro sjednání digitálním podpisem, musí být oddělena od dalších informací */}
                  {sjednaniInfoData && sjednaniInfoData.length > 0 &&
                        <CompletedOverview
                          items={sjednaniInfoData}
                          sx={{ flex: '1' }}
                          {...sjednaniInfoProps}
                        />
                  }
                  {
                    additionalInfoData &&
                        additionalInfoData.length > 0 &&
                        sjednaniInfoData &&
                        sjednaniInfoData.length > 0 &&
                        <Divider />
                  }

                  {
                    additionalInfoData &&
                        additionalInfoData.length > 0 &&
                        <CompletedOverview
                          items={additionalInfoData}
                          sx={{
                            flex: '1'
                            // h: '100%',
                            // gridTemplateColumns: { base: '1fr', md: '1fr' },
                          }}
                          {...additionalInfoProps}
                        />
                  }
                </>
      }

      {selectedTab == InformationTab.Recap &&

                <Box sx={{
                  pl: {
                    base: 0,
                    md: 16,
                  }
                }}>
                  {Apps.property === app &&
                        <PropertyRecapTable offer={offer as OfferProperty} />
                  }

                  {Apps.property !== app &&
                        <RecapTable {...recapProps} />
                  }
                </Box>


      }

      {selectedTab == InformationTab.AdditionalDates &&
                <CompletedOverview
                  //ref={completedOverviewRef}
                  items={additionalDatesTabData}
                  {...additionalDatesTabProps}
                />
      }

      <Divider sx={{ alignSelf: 'end', justifySelf: 'end', mt: 'auto' }} />

      <Grid>
        <DateSelector
          sx={{ mx: 'auto' }}
          options={tabs}
          value={selectedTab}
          onChange={(value) => setSetselectedTab(value)}
        />
      </Grid>
    </RecapSummary>
  )
}